/**
 * @fileoverview Enhanced API service that combines authentication and quota handling
 * Integrates with existing apiInterceptor and adds quota detection
 */

import { QuotaExceededError, normalizeQuotaPayload } from '../lib/quota.js';
import { apiRequest as originalApiRequest } from './apiInterceptor.js';

/**
 * Enhanced API request function with both auth and quota handling
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} Response data
 * @throws {QuotaExceededError} When quota is exceeded
 * @throws {Error} For other API errors
 */
async function enhancedApiRequest(endpoint, options = {}) {
  try {
    return await originalApiRequest(endpoint, options);
  } catch (error) {
    // Check if this is a 403 error that might be quota-related
    if (error && (error.status === 403 || (error.response && error.response.status === 403))) {
      // Try to extract quota information from the error
      let quotaPayload = null;
      
      // Check various places where the quota payload might be
      if (error.data) {
        quotaPayload = error.data;
      } else if (error.response && error.response.data) {
        quotaPayload = error.response.data;
      } else if (error.body) {
        quotaPayload = error.body;
      }
      
      // If we have a payload that looks like a quota error, convert it
      if (quotaPayload && 
          (quotaPayload.title === 'Plan Limit Exceeded' ||
           quotaPayload.detail || // Include responses with detail field (e.g., plan not enabled messages)
           quotaPayload.usageMetrics ||
           quotaPayload.UsageMetrics || // Handle capital case from API
           quotaPayload.limit !== undefined ||
           quotaPayload.usage !== undefined)) {
        
        const normalizedQuota = normalizeQuotaPayload(quotaPayload);
        // Add endpoint context to help with debugging and user messages
        normalizedQuota.endpoint = endpoint;
        
        throw new QuotaExceededError(normalizedQuota);
      }
    }
    
    // Re-throw the original error if it's not quota-related
    // This preserves auth error handling and other error types
    throw error;
  }
}

/**
 * Enhanced HTTP client wrapper with both auth and quota detection
 * Provides convenient methods for different HTTP verbs
 */
const enhancedApi = {
  /**
   * GET request with auth and quota detection
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @returns {Promise<any>} Response data
   */
  async get(endpoint, params = {}) {
    const queryString = createQueryString(params);
    return enhancedApiRequest(`${endpoint}${queryString}`, { method: 'GET' });
  },

  /**
   * POST request with auth and quota detection
   * @param {string} endpoint - API endpoint
   * @param {any} data - Request body data
   * @returns {Promise<any>} Response data
   */
  async post(endpoint, data) {
    return enhancedApiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },

  /**
   * PUT request with auth and quota detection
   * @param {string} endpoint - API endpoint
   * @param {any} data - Request body data
   * @returns {Promise<any>} Response data
   */
  async put(endpoint, data) {
    return enhancedApiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },

  /**
   * DELETE request with auth and quota detection
   * @param {string} endpoint - API endpoint
   * @returns {Promise<any>} Response data
   */
  async delete(endpoint) {
    return enhancedApiRequest(endpoint, { method: 'DELETE' });
  },

  /**
   * PATCH request with auth and quota detection
   * @param {string} endpoint - API endpoint
   * @param {any} data - Request body data
   * @returns {Promise<any>} Response data
   */
  async patch(endpoint, data) {
    return enhancedApiRequest(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }
};

/**
 * Creates query string from parameters object
 * @param {Object} params - Object containing query parameters
 * @returns {string} - Formatted query string
 */
function createQueryString(params) {
  if (!params || Object.keys(params).length === 0) return '';
  
  const queryParams = [];
  
  for (const [key, value] of Object.entries(params)) {
    // Skip null or undefined values
    if (value === null || value === undefined) continue;
    
    // Handle arrays
    if (Array.isArray(value)) {
      value.forEach(item => {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
      });
    } else {
      queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  
  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
}

// Export both the request function and the API object
export { enhancedApiRequest as apiRequest, enhancedApi as api };
export default enhancedApi;
